import type { LatLng } from '@simpleacs/shared';

/**
 * Corner edits on a saved zone ring — the pure half of the map's "Edit
 * corners" draw mode (operator, 2026-09-16: make topology easier; move a
 * corner instead of redrawing the whole boundary from scratch).
 *
 * Rings are open, like everywhere else in shared `geo.ts`: the closing edge
 * from the last point back to the first is implied, so a ring of n points
 * has n edges and n midpoints. Every function returns a NEW array and leaves
 * the input alone, except where it refuses — then it hands the SAME array
 * back, and that identity is the refusal signal the map reads (`next ===
 * ring` means nothing changed, so nothing goes on the undo stack).
 *
 * Nothing here judges the ring against its neighbours: that is
 * `redrawWithNeighbours`, the shared function the API saves with, and the
 * map runs it after every edit exactly as it does after every drawn corner.
 */

/** A boundary keeps at least three corners — fewer is a line, not a ring (`validRing` says the same). */
export const MIN_RING_CORNERS = 3;

const inRange = (ring: LatLng[], i: number): boolean => Number.isInteger(i) && i >= 0 && i < ring.length;

/** Corner `i` dragged to `point`. A handle from a ring that has since changed shape (stale index) moves nothing. */
export function moveVertex(ring: LatLng[], i: number, point: LatLng): LatLng[] {
  if (!inRange(ring, i)) return ring;
  return ring.map((p, k) => (k === i ? point : p));
}

/**
 * A new corner after corner `afterIndex` — on the edge from it to the next
 * corner, which for the last index is the implied closing edge. The order of
 * the ring is what makes it a polygon, so the point goes exactly there and
 * never at the end.
 */
export function insertVertex(ring: LatLng[], afterIndex: number, point: LatLng): LatLng[] {
  if (!inRange(ring, afterIndex)) return ring;
  return [...ring.slice(0, afterIndex + 1), point, ...ring.slice(afterIndex + 1)];
}

/** Corner `i` removed. Refused — the same ring back — when only three remain, or the index is stale. */
export function deleteVertex(ring: LatLng[], i: number): LatLng[] {
  if (!inRange(ring, i) || ring.length <= MIN_RING_CORNERS) return ring;
  return ring.filter((_, k) => k !== i);
}

/**
 * The midpoint of every edge, closing edge included: `midpoints(ring)[i]`
 * sits on the edge that starts at corner `i`, so dragging it is
 * `insertVertex(ring, i, …)`. Fewer than two corners have no edge.
 */
export function midpoints(ring: LatLng[]): LatLng[] {
  if (ring.length < 2) return [];
  return ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  });
}
